import * as React from 'react';
import styled from 'styled-components';
import {useRoute} from 'react-router5';
import {CourseObject, CourseStatus, editCourse, getStatusLabel, isPublished,} from 'api/course';
import CoursePreview, {CoursePreviewProps} from 'components/CoursePreview';
import ControlButton from 'components/ControlButton';
import Switch from 'components/Switch';
import TotalPrice from 'components/TotalPrice';
import Button, {ButtonVariant} from './components/Button';
import useModal from 'hooks/use-modal';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {
  IconChevronBottom,
  IconCopy,
  IconEdit,
  IconInvite,
  IconNotActiveFolder,
  IconShare,
  IconStudentList,
  IconTrash,
} from 'icons';
import {toast} from 'react-toastify';
import ButtonLink from "../ButtonLink";
import {ButtonVariant as BaseButtonVariant} from "../Button";
import {getUserByID} from "api/user/requests";
import {getPrices} from "components/CoursePromo/helpers";
import ProgramCardUsageList from "components/ProgramCardUsageList";
import {CardView} from "hooks/use-selected-view";
import copyToClipboard from "helpers/copy-to-clipboard";
import {InviteModal} from '../InviteModal';

export interface TeacherCoursePreviewProps
  extends CoursePreviewProps {
  onCopy?(id: CourseObject['id']): void;
  onDelete?(id: CourseObject['id']): void;
}

export const CourseHeader = styled.div`
  margin-bottom: 6px;
  justify-content: space-between;
  align-items: center;
  grid-auto-flow: column;
  display: grid;
`;

const Controls = styled.div`
  align-items: center;
  grid-auto-flow: column;
  display: inline-grid;
`;

export const CourseFooter = styled.div`
  justify-content: space-between;
  align-items: flex-end;
  display: flex;
`;

const Buttons = styled.div`
  gap: 8px;
  grid-auto-flow: column;
  display: inline-grid;
`;

const CourseLinkToast = styled.div`
  display: grid;
  grid-template-columns: max-content;
  gap: 8px;
`;

const UsageListWrapper = styled.div`
  position: relative;
`;

const UsageListBox = styled.div`
  position: absolute;
  top: calc(100% + 15px);
  right: -125px;
  z-index: 100;
`;

const TotalPriceWrapper = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
`;

/**
 * This component renders a preview of the course from the teacher's view.
 * @param {TeacherCoursePreviewProps} props
 * @return {React.ReactElement<TeacherCoursePreviewProps>}
 * @constructor
 */
export default function TeacherCoursePreview(
    props: TeacherCoursePreviewProps,
): React.ReactElement<TeacherCoursePreviewProps> {
  const {router} = useRoute();

  const [
    opened,
    setOpened,
  ] = React.useState(false)
  const [
    courseLink,
    setCourseLink,
  ] = React.useState<string | undefined>()

  const [
    isStudentsModal,
    setStudentsModal
  ] = React.useState(false)

  const inviteModal = useModal();

  React.useEffect(() => {
    new Promise((resolve) => {
      if (course.userID) {
        getUserByID(course.userID).then((data) => {
          validateResponse(data);
          if (data && !isError(data)) {
            resolve(data.username)
          }
        })
      } else {

      }
    }).then(userName => userName && setCourseLink(`teachers/${userName}/courses/${course.slug}`))
  }, [])

  const [
    course,
    setCourse,
  ] = React.useState<CourseObject>(props);

  const [usageListOpened, setUsageListOpened] = React.useState(false)

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    /**
     * Render status.
     * @return {JSX.Element | null}
     */
    function renderStatus(): JSX.Element | null {
      /**
       * This click event handler.
       */
      function handleClick(): void {
        editCourse({
          ...props,
          status: course.status === CourseStatus.Public ? (
            CourseStatus.Draft
          ) : (
            CourseStatus.Public
          ),
        }).then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            setCourse(response);
          }
        });
      }

      if (props.view === CardView.ShortList && !opened) {
        return null;
      }

      return (
        <Switch
          name="status"
          label={getStatusLabel(course.status)}
          checked={isPublished(course.status)}
          onClick={handleClick}
        />
      );
    }

    /**
     * Render controls.
     * @return {JSX.Element}
     */
    function renderControls(): JSX.Element {
      /**
       * Render usage list control.
       * @return {JSX.Element}
       */
      function renderUsageControl(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          setUsageListOpened((prev) => !prev)
        }

        /**
         * render usage list
         */
        function renderUsageList(): JSX.Element | void {
          if (usageListOpened) {
            return (
              <UsageListBox>
                <ProgramCardUsageList
                  type="folders"
                  title="Используется в папке ..."
                  itemId={props.id}
                  onIconCloseClick={() => setUsageListOpened(false)}
                />
              </UsageListBox>
            )
          }
        }

        return (
          <UsageListWrapper>
            <ControlButton onClick={handleClick}>
              <IconNotActiveFolder />
            </ControlButton>
            {renderUsageList()}
          </UsageListWrapper>
        );
      }
      /**
       * Render edit control.
       * @return {JSX.Element}
       */
      function renderEditControl(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          router.navigate('courses.course.edit', {
            id: course.id,
          });
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconEdit />
          </ControlButton>
        );
      }

      /**
       * Render copy control.
       * @return {JSX.Element}
       */
      function renderCopyControl(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          props.onCopy && props.onCopy(course.id);
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconCopy />
          </ControlButton>
        );
      }

      /**
       * Render trash control.
       * @return {JSX.Element}
       */
      function renderTrashControl(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          props.onDelete && props.onDelete(course.id);
        }

        return (
          <ControlButton onClick={handleClick}>
            <IconTrash />
          </ControlButton>
        );
      }

      /**
       * Render toggle open icon.
       * @return {JSX.Element | void}
       */
      function renderToggleOpen(): JSX.Element | void {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          setOpened(prev => !prev)
        }

        if (props.view === CardView.ShortList) {
          return (
            <ControlButton onClick={handleClick}>
              <IconChevronBottom />
            </ControlButton>
          );
        }
      }

      return (
        <Controls>
          {renderUsageControl()}
          {renderEditControl()}
          {renderCopyControl()}
          {renderTrashControl()}
          {renderToggleOpen()}
        </Controls>
      );
    }

    return (
      <CourseHeader>
        {renderStatus()}
        {renderControls()}
      </CourseHeader>
    );
  }

  /**
   * the function open new browser tab with course promo page
   */
  function openCoursePromo() {
    window.open(courseLink, '_blank')
  }

  /**
   * render course link toast
   * @return {JSX.Element}
   */
  function renderCourseLinkToast() {
    return (
      <CourseLinkToast>
        <div>Скопирована ссылка на курс</div>
        <ButtonLink
          variant={BaseButtonVariant.Default}
          onClick={openCoursePromo}
        >Открыть</ButtonLink>
      </CourseLinkToast>
    )
  }

  /**
   * Render footer.
   * @return {JSX.Element}
   */
  function renderFooter(): JSX.Element {
    /**
     * Render buttons.
     * @return {JSX.Element}
     */
    function renderButtons(): JSX.Element {
      /**
       * Render share button.
       * @return {JSX.Element}
       */
      function renderShareButton(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          if (courseLink) {
            toast.success(renderCourseLinkToast());
            const linkPrefix = window.location.host + '/';
            copyToClipboard(linkPrefix + courseLink)
          } else {
            toast.error('Ошибка! Попробуйте позже')
          }
        }

        return (
          <Button onClick={handleClick}>
            <IconShare />
          </Button>
        );
      }

      /**
       * Render students button.
       * @return {JSX.Element}
       */
      function renderStudentsButton(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          setStudentsModal(true)
        }

        return (
          <Button onClick={handleClick}>
            <IconStudentList />
          </Button>
        );
      }

      /**
       * Render invite button.
       * @return {JSX.Element}
       */
      function renderInviteButton(): JSX.Element {
        /**
         * This click event handler.
         */
        function handleClick(): void {
          inviteModal.setModalVisible(
            !inviteModal.isModalVisible
          );
        }

        return (
          <React.Fragment>
            <Button
              variant={ButtonVariant.Dashed}
              onClick={handleClick}
            >
              <IconInvite />
            </Button>
            {inviteModal.isModalVisible && (
              <InviteModal
                courseId={props.id}
                onBlur={() => (
                  inviteModal.setModalVisible(false)
                )}
              />
            )}
          </React.Fragment>
        );
      }

      return (
        <Buttons>
          {renderShareButton()}
          {renderStudentsButton()}
          {renderInviteButton()}
        </Buttons>
      );
    }

    /**
     * Render total price.
     * @return {JSX.Element | undefined}
     */
    function renderTotalPrice(): JSX.Element | undefined {
      const Wrapper = getTotalPriceWrapper();

      if (course.price?.currency && course.price?.amount) {
        return (
          <Wrapper>
            <TotalPrice
              price={{
                currency: course.price.currency,
                amount: course.price.amount,
              }}
              sale={getTotalPriceSale()}
            />
          </Wrapper>
        )
      }
    }

    /**
     * the function return total price wrapper
     */
    function getTotalPriceWrapper() {
      const isStyledWrapRequired = props.view === CardView.List
        || props.view === CardView.ShortList;

      return isStyledWrapRequired
        ? TotalPriceWrapper
        : 'div'
    }

    /**
     * the function return price sale if installment is available
     * @return {number | undefined}
     */
    function getTotalPriceSale(): number | undefined {
      const prices = getPrices(course)

      if (prices.isInstallmentAvailable && (prices.old !== undefined)) {
        return prices.old - prices.current
      }
    }

    return (
      <CourseFooter>
        {renderButtons()}
        {renderTotalPrice()}
      </CourseFooter>
    );
  }

  return (
    <React.Fragment>
      <CoursePreview
        {...props}
        opened={opened}
        renderHeader={renderHeader()}
        renderFooter={renderFooter()}

        studentsModalOpened={isStudentsModal}
        studentModalClose={() => setStudentsModal(false)}
      />
    </React.Fragment>
  );
}
